







































import { defineComponent, computed, ref } from '@nuxtjs/composition-api'
import DatasheetTable from './DatasheetTable.vue'
import { PRODUCT_TYPES } from '~/common/utils/product/constants'
import { KitComponent, MappedProduct } from '~/types/product/productTypes'
import { usePdpStore } from '~/store/pdp'
import { isSignalStarSecondaryAntibodyKit } from '~/common/utils/product/product'
import { RteNode } from '~/types/rte/rteTypes'
import JsonRteField from '~/components/global/cms/JsonRteField.vue'

const DATASHEET_KIT_ATTRIBUTES = {
  NAME: {
    label: 'Product Includes',
    getValue: (component: KitComponent) => component.name ?? '',
  },
  NUMBER: {
    label: 'Product #',
    getValue: (component: KitComponent) => component.number ?? '',
  },
  QUANTITY: {
    label: 'Quantity',
    getValue: (component: KitComponent) =>
      component.quantity && component.unit ? `${component.quantity} ${component.unit}` : '',
  },
  QUANTITY_WITH_COUNT: {
    label: 'Quantity',
    getValue: (component: KitComponent) =>
      component.count && component.quantity && component.unit
        ? `${component.count} x ${component.quantity} ${component.unit}`
        : '',
  },
  VOLUME: {
    label: 'Volume',
    getValue: (component: KitComponent) => DATASHEET_KIT_ATTRIBUTES.QUANTITY.getValue(component),
  },
  COLOR: {
    label: 'Color',
    getValue: (component: KitComponent) => component['solution-color'] ?? '',
  },
  CAP_COLOR: {
    label: 'Cap Color',
    getValue: (component: KitComponent) => component['cap-color'] ?? '',
  },
  ISOTYPE_SOURCE: {
    label: 'Isotype/Source',
    getValue: (component: KitComponent) => `${component.host ?? ''} ${component.isotype ?? ''}`,
  },
  MOLECULAR_WEIGHT: {
    label: 'Mol. Wt',
    getValue: (component: KitComponent) =>
      component['molecular-weight-summary'] ? component['molecular-weight-summary'] + ' kDa' : '',
  },
  REACTIVITY: {
    label: 'Reactivity',
    getValue: (component: KitComponent) => component.lot?.reactivity?.reacts.map((r) => r.code).join(' ') ?? '',
  },
  STORAGE_TEMP: {
    label: 'Storage Temp',
    getValue: (component: KitComponent) => component['component-storage-temp'] ?? '',
  },
  ISOTYPE: {
    label: 'Isotype',
    getValue: (component: KitComponent) => DATASHEET_KIT_ATTRIBUTES.ISOTYPE_SOURCE.getValue(component),
  },
} as const

type DatasheetKitAttribute = (typeof DATASHEET_KIT_ATTRIBUTES)[keyof typeof DATASHEET_KIT_ATTRIBUTES]

export default defineComponent({
  components: {
    DatasheetTable,
    JsonRteField,
  },
  props: {
    product: {
      type: Object as () => MappedProduct,
      required: true,
    },
  },
  setup(props) {
    const customProductIncludes = usePdpStore().pdpPages[props.product.key]
    const productType = computed(() => props.product.productType)

    const footnotes = computed(() => props.product.attributes.kits?.footnotes?.footnote || [])

    const tableData = computed(() => {
      let attributes: DatasheetKitAttribute[] | undefined

      if (productType.value === PRODUCT_TYPES.FAST_SCAN_ELISA_KIT) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.QUANTITY,
          DATASHEET_KIT_ATTRIBUTES.COLOR,
        ]
      } else if (productType.value === PRODUCT_TYPES.ELISA_ANTIBODY_PAIR) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.VOLUME,
          DATASHEET_KIT_ATTRIBUTES.CAP_COLOR,
          DATASHEET_KIT_ATTRIBUTES.STORAGE_TEMP,
        ]
      } else if (productType.value === PRODUCT_TYPES.ELISA_KIT) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.QUANTITY,
          DATASHEET_KIT_ATTRIBUTES.COLOR,
          DATASHEET_KIT_ATTRIBUTES.STORAGE_TEMP,
        ]
      } else if (productType.value === PRODUCT_TYPES.MATCHED_ANTIBODY_PAIR) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.QUANTITY,
          DATASHEET_KIT_ATTRIBUTES.ISOTYPE_SOURCE,
        ]
      } else if (productType.value === PRODUCT_TYPES.ANTIBODY_SAMPLER_KIT) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.QUANTITY,
          DATASHEET_KIT_ATTRIBUTES.MOLECULAR_WEIGHT,
          DATASHEET_KIT_ATTRIBUTES.ISOTYPE_SOURCE,
        ]
      } else if (productType.value === PRODUCT_TYPES.OLIGO_ANTIBODY_PAIR) {
        // SignalStar Antibody Kit is not based on the product type, but it has unique condition based on product type and specific string in name
        attributes = isSignalStarSecondaryAntibodyKit(props.product)
          ? [
              DATASHEET_KIT_ATTRIBUTES.NAME,
              DATASHEET_KIT_ATTRIBUTES.NUMBER,
              DATASHEET_KIT_ATTRIBUTES.QUANTITY_WITH_COUNT,
              DATASHEET_KIT_ATTRIBUTES.ISOTYPE,
            ]
          : [
              DATASHEET_KIT_ATTRIBUTES.NAME,
              DATASHEET_KIT_ATTRIBUTES.NUMBER,
              DATASHEET_KIT_ATTRIBUTES.VOLUME,
              DATASHEET_KIT_ATTRIBUTES.REACTIVITY,
              DATASHEET_KIT_ATTRIBUTES.ISOTYPE,
            ]
      } else if (productType.value === PRODUCT_TYPES.ANTIBODY_DUET) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.QUANTITY,
          DATASHEET_KIT_ATTRIBUTES.MOLECULAR_WEIGHT,
          DATASHEET_KIT_ATTRIBUTES.ISOTYPE_SOURCE,
        ]
      } else if (
        productType.value === PRODUCT_TYPES.CELL_EXTRACT_KIT ||
        productType.value === PRODUCT_TYPES.PROTEIN_CONTROL_KIT
      ) {
        attributes = [DATASHEET_KIT_ATTRIBUTES.NAME, DATASHEET_KIT_ATTRIBUTES.NUMBER, DATASHEET_KIT_ATTRIBUTES.QUANTITY]
      } else if (productType.value === PRODUCT_TYPES.PTM_SCAN) {
        attributes = [
          DATASHEET_KIT_ATTRIBUTES.NAME,
          DATASHEET_KIT_ATTRIBUTES.NUMBER,
          DATASHEET_KIT_ATTRIBUTES.QUANTITY_WITH_COUNT,
          DATASHEET_KIT_ATTRIBUTES.STORAGE_TEMP,
        ]
      }

      if (productType.value === PRODUCT_TYPES.DETECTION_SYSTEM_KIT) {
        const headings = ['Product Includes', 'Product #'] as any
        const rows = [] as any

        props.product.attributes.kits?.component?.map((kit) => {
          headings.push(`Quantity (${kit.size?.text})`)

          if (rows.find((item: any) => item[0] === kit.name)) {
            const rowIndex = rows.findIndex((item: any) => item[0] === kit.name)
            rows[rowIndex] = [
              ...new Set(rows[rowIndex].concat([kit.name, kit.number, `${kit.count} x ${kit.quantity} ${kit.unit}`])),
            ]
          } else {
            rows.push([kit.name, kit.number, `${kit.count} x ${kit.quantity} ${kit.unit}`])
          }

          return null
        })

        return {
          headings: [...new Set(headings)],
          rows,
        }
      } else
        return {
          headings: attributes?.map((a) => a.label),
          rows: attributes
            ? props.product.attributes.kits?.component?.map((kit) => attributes!.map((a) => a.getValue(kit)))
            : undefined,
        }
    })

    const customProductIncludesNode = ref<RteNode | undefined>(
      customProductIncludes?.components?.at(0)?.pdp_product_inquiry?.content?.json
    )

    return {
      tableData,
      productType,
      footnotes,
      PRODUCT_TYPES,
      customProductIncludesNode,
    }
  },
})
